import { Position } from 'reactflow';
import { WorkflowNodeType, ConnectionType } from './types';

// Handle type definitions
export interface HandleDefinition {
  id: string;
  type: 'source' | 'target';
  position: 'top' | 'right' | 'bottom' | 'left';
  label: string;
  style?: any;
}

// Define all handles for each node type
export const NODE_HANDLES: Record<WorkflowNodeType, HandleDefinition[]> = {
  [WorkflowNodeType.PHASE]: [
    { id: 'input', type: 'target', position: 'top', label: 'Input' },
    { id: 'output', type: 'source', position: 'bottom', label: 'Output' },
    { id: 'tools', type: 'target', position: 'left', label: 'Tools' },
    { id: 'personas', type: 'target', position: 'right', label: 'Personas' },
    { id: 'reviewer', type: 'target', position: 'right', label: 'Reviewer' },
    { id: 'artifacts_in', type: 'target', position: 'top', label: 'Artifacts In' },
    { id: 'artifacts_out', type: 'source', position: 'bottom', label: 'Artifacts Out' }
  ],
  [WorkflowNodeType.TOOL]: [
    { id: 'tool_output', type: 'source', position: 'right', label: 'Tool' }
  ],
  [WorkflowNodeType.PERSONA]: [
    { id: 'persona_output', type: 'source', position: 'left', label: 'Persona' }
  ],
  [WorkflowNodeType.ARTIFACT]: [
    { id: 'artifact_output', type: 'source', position: 'bottom', label: 'Output' },
    { id: 'artifact_input', type: 'target', position: 'top', label: 'Input' }
  ]
};

// Define valid connections between node types and handles
export interface ConnectionRule {
  sourceType: WorkflowNodeType;
  sourceHandle: string;
  targetType: WorkflowNodeType;
  targetHandle: string;
  connectionType: ConnectionType;
  style?: any;
}

export const CONNECTION_RULES: ConnectionRule[] = [
  // Phase to Phase connections
  {
    sourceType: WorkflowNodeType.PHASE,
    sourceHandle: 'output',
    targetType: WorkflowNodeType.PHASE,
    targetHandle: 'input',
    connectionType: ConnectionType.FLOW,
    style: { stroke: '#0088ff', strokeWidth: 2 }
  },
  // Phase to Phase artifact connections
  {
    sourceType: WorkflowNodeType.PHASE,
    sourceHandle: 'artifacts_out',
    targetType: WorkflowNodeType.PHASE,
    targetHandle: 'artifacts_in',
    connectionType: ConnectionType.FLOW,
    style: { stroke: '#9333ea', strokeWidth: 2 }
  },
  // Tool to Phase connections
  {
    sourceType: WorkflowNodeType.TOOL,
    sourceHandle: 'tool_output',
    targetType: WorkflowNodeType.PHASE,
    targetHandle: 'tools',
    connectionType: ConnectionType.TOOL_ASSIGNMENT,
    style: { stroke: '#00aa00', strokeWidth: 2 }
  },
  // Persona to Phase connections
  {
    sourceType: WorkflowNodeType.PERSONA,
    sourceHandle: 'persona_output',
    targetType: WorkflowNodeType.PHASE,
    targetHandle: 'personas',
    connectionType: ConnectionType.PERSONA_ASSIGNMENT,
    style: { stroke: '#aa00aa', strokeWidth: 2 }
  },
  // Persona to Phase reviewer connections
  {
    sourceType: WorkflowNodeType.PERSONA,
    sourceHandle: 'persona_output',
    targetType: WorkflowNodeType.PHASE,
    targetHandle: 'reviewer',
    connectionType: ConnectionType.PERSONA_ASSIGNMENT,
    style: { stroke: '#9333ea', strokeWidth: 2 }
  },
  // Artifact to Phase connections (input)
  {
    sourceType: WorkflowNodeType.ARTIFACT,
    sourceHandle: 'artifact_output',
    targetType: WorkflowNodeType.PHASE,
    targetHandle: 'artifacts_in',
    connectionType: ConnectionType.INPUT_ARTIFACT,
    style: { stroke: '#9333ea', strokeWidth: 2 }
  },
  // Artifact to Phase input connections (alternative input)
  {
    sourceType: WorkflowNodeType.ARTIFACT,
    sourceHandle: 'artifact_output',
    targetType: WorkflowNodeType.PHASE,
    targetHandle: 'input',
    connectionType: ConnectionType.INPUT_ARTIFACT,
    style: { stroke: '#9333ea', strokeWidth: 2 }
  },
  // Phase to Artifact connections (output)
  {
    sourceType: WorkflowNodeType.PHASE,
    sourceHandle: 'artifacts_out',
    targetType: WorkflowNodeType.ARTIFACT,
    targetHandle: 'artifact_input',
    connectionType: ConnectionType.OUTPUT_ARTIFACT,
    style: { stroke: '#9333ea', strokeWidth: 2 }
  }
];

// Helper function to check if a connection is valid
export function isValidConnection(
  sourceType: WorkflowNodeType,
  sourceHandle: string,
  targetType: WorkflowNodeType,
  targetHandle: string
): ConnectionRule | null {
  return CONNECTION_RULES.find(
    rule =>
      rule.sourceType === sourceType &&
      rule.sourceHandle === sourceHandle &&
      rule.targetType === targetType &&
      rule.targetHandle === targetHandle
  ) || null;
}

// Helper function to get handle by ID for a node type
export function getHandleById(nodeType: WorkflowNodeType, handleId: string): HandleDefinition | null {
  const handles = NODE_HANDLES[nodeType] || [];
  return handles.find(h => h.id === handleId) || { id: handleId, type: 'source', position: 'bottom', label: handleId } as HandleDefinition;
}

// Legacy handle mapping (for backward compatibility)
export const LEGACY_HANDLE_MAPPING: Record<string, Record<string, string>> = {
  [WorkflowNodeType.TOOL]: {
    'right': 'tool_output'
  },
  [WorkflowNodeType.PERSONA]: {
    'left': 'persona_output'
  },
  [WorkflowNodeType.ARTIFACT]: {
    'right': 'artifact_output',
    'left': 'artifact_input'
  }
};

// Helper function to map legacy handle IDs to new semantic ones
export function mapLegacyHandleId(nodeType: WorkflowNodeType, handleId: string): string {
  if (LEGACY_HANDLE_MAPPING[nodeType] && LEGACY_HANDLE_MAPPING[nodeType][handleId]) {
    return LEGACY_HANDLE_MAPPING[nodeType][handleId];
  }
  return handleId;
}

// Helper function to get ReactFlow Position from our position string
export function getReactFlowPosition(position: string): Position {
  switch (position.toLowerCase()) {
    case 'top': return Position.TOP;
    case 'right': return Position.RIGHT;
    case 'bottom': return Position.BOTTOM;
    case 'left': return Position.LEFT;
    default: return Position.TOP;
  }
}

// Export the handle definitions
export default NODE_HANDLES;
